import React from 'react';
import {
  PaymentCardForm,
  type PaymentCardFormProps,
} from '@cloudbeds/payment-tokenization';

import { queryPaymentMethodHash } from '../../../queries/methodHash';
import { useCommonOptionsStore } from '../../../store';
import { tokenexPaymentMethodHashPayloadFactory } from '../../../factories/query-payment-method-hash-payload';
import { useNextActionHandler } from '../../../handlers/nextActionHandler';

import type { NextAction, PaymentElementResponse } from '../../../@types';
import type { CreditCardFormProps } from './creditCard.types';

export const TokenexCardForm = React.forwardRef(
  (cardProps: CreditCardFormProps, ref: any) => {
    const lang = useCommonOptionsStore(state => state.lang);
    const propertyId = useCommonOptionsStore(state => state.propertyId);
    const apiKey = useCommonOptionsStore(state => state.apiKey);
    const { apiUrl } = useCommonOptionsStore();
    const nextActionHandler = useNextActionHandler();

    const props: PaymentCardFormProps = React.useMemo(() => {
      const { firstName = '', lastName = '' } = cardProps.guest || {};

      return {
        validateOnChange: false,
        validateOnBlur: false,
        onLoadStart: () => {},
        onLoadEnd: () => {},
        onAuthExpired: () => {},
        mode: cardProps?.mode || 'live',
        lang,
        defaultValues: {
          firstName,
          lastName,
          fullName: `${firstName} ${lastName}`.trim(),
          ...(cardProps.guest || {}),
        },
        onSubmitHandler: async (data: any) => {
          /** Payment method hash data object */
          const hashPayload = tokenexPaymentMethodHashPayloadFactory(data);
          const { hash: paymentMethodHash, error: hashError } =
            await queryPaymentMethodHash(
              +propertyId,
              apiKey,
              hashPayload,
              apiUrl,
            );
          if (hashError && cardProps.onError) cardProps.onError(hashError);

          const responseData: PaymentElementResponse = {
            ...data,
            hashPayload,
            ...(data.cvv && !data.cvc && { cvc: { ...data.cvv } }),
            paymentMethod: 'card_tokenex',
            paymentMethodHash,
          };

          const nextAction: NextAction = await cardProps.onSubmit({
            ...responseData,
          });
          await nextActionHandler(nextAction);
        },
        onErrorHandler: (error: any) => {
          if (cardProps.onError) cardProps.onError({ ...error });
        },
        onValidationErrorHandler: (error: any) => {
          if (cardProps.onValidationError)
            cardProps.onValidationError({ ...error });
        },
        labels: cardProps?.labels ?? {},
      };
    }, [lang, propertyId, apiKey, cardProps, apiUrl]);

    return (
      <PaymentCardForm
        {...props}
        cardBrands={cardProps.paymentMethodConfig?.brands ?? null}
        ref={ref}
      />
    );
  },
);
